import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Stack,
    Text,
    Textarea,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    IconButton,
    Tooltip,
    Spinner,
    InputGroup,
    InputRightElement,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from '@chakra-ui/react';
import { InfoIcon, CheckCircleIcon, WarningIcon, DeleteIcon } from '@chakra-ui/icons';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import SelectCountry from '../SelectCountry/SelectCountry';
import InvoiceDetailsForm from '../InvoiceDetailsForm/InvoiceDetailsForm';
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import isEqual from 'lodash.isequal';
import { useAppToast } from "../../../../hooks/useAppToast";
import KsefService from '../../../../services/ksef/ksefService';
import { useState, useEffect, useRef } from 'react';

const CompanyDetailsForm = ({ onSuccess, config, user }) => {
    const { t } = useTranslation('default');
    const { setup } = useAuth();
    const toast = useAppToast();

    const [isKsefMarkedForDeletion, setIsKsefMarkedForDeletion] = useState(false);
    const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
    const cancelRef = useRef();

    const initialValues = {
        companyName: config?.companyName || '',
        nip: config?.nip || '',
        country: config?.country || '',
        postalCode: config?.postalCode || '',
        city: config?.city || '',
        street: config?.street || '',
        building: config?.building || '',
        apartment: config?.apartment || '',
        krs: config?.krs || '',
        regon: config?.regon || '',
        comments: config?.comments || '',
        payment: config?.payment || '',
        paymentSecret: config?.paymentSecret || '',
        ksefToken: '', // Zostawiamy puste, aby nie wysyłać zamaskowanego tokenu (kropek) do API
        invoice: {
            companyName: config?.invoice?.companyName || '',
            nip: config?.invoice?.nip || '',
            country: config?.invoice?.country || '',
            postalCode: config?.invoice?.postalCode || '',
            city: config?.invoice?.city || '',
            street: config?.invoice?.street || '',
            building: config?.invoice?.building || '',
            apartment: config?.invoice?.apartment || '',
            email: config?.invoice?.email || user?.email || '',
            krs: config?.invoice?.krs || '',
            regon: config?.invoice?.regon || '',
            comments: config?.invoice?.comments || '',
            vatPayer: config?.invoice?.vatPayer || 'false'
        }
    };
    const isValidNIP = (nip) => {
        const digits = nip.replace(/[^0-9]/g, '');
        if (digits.length !== 10) return false;
        const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
        const sum = weights.reduce((acc, weight, i) => acc + weight * +digits[i], 0);
        return (sum % 11) === +digits[9];
    };

    const isValidPostalCode = (value) => {
        return /^[0-9]{2}-[0-9]{3}$/.test(value);
    };

    const isValidKRS = (value) => {
        return (value || '') === '' || (/^\d{10}$/.test(value));
    };

    const isValidREGON = (value) => {
        if ((value || '') === '') return true;
        if (!/^\d{9}$|^\d{14}$/.test(value)) return false;

        const weights9 = [8, 9, 2, 3, 4, 5, 6, 7];
        const weights14 = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8];

        const digits = value.split('').map(Number);
        const controlDigit = digits.pop();

        const weights = value.length === 9 ? weights9 : weights14;
        const sum = weights.reduce((acc, w, i) => acc + w * digits[i], 0);
        const remainder = sum % 11;
        const expected = remainder === 10 ? 0 : remainder;

        return controlDigit === expected;
    };

    const validationSchema = Yup.object({
        companyName: Yup.string().required(t('setup.form.companyName.required')),
        nip: Yup.string()
            .required(t('setup.form.nip.required'))
            .test('valid-nip', t('setup.form.nip.invalid'), isValidNIP),
        country: Yup.string().required(t('setup.form.country.required')),
        postalCode: Yup.string()
            .required(t('setup.form.postalCode.required'))
            .test('valid-postal', t('setup.form.postalCode.invalid'), isValidPostalCode),
        city: Yup.string().required(t('setup.form.city.required')),
        street: Yup.string().required(t('setup.form.street.required')),
        building: Yup.string().required(t('setup.form.building.required')),
        payment: Yup.string(),
        paymentSecret: Yup.string(),
        ksefToken: Yup.string(),
        krs: Yup.string().test('valid-krs', t('setup.form.krs.invalid'), isValidKRS),
        regon: Yup.string().test('valid-regon', t('setup.form.regon.invalid'), isValidREGON),

        invoice: Yup.object({
            companyName: Yup.string().required(t('setup.form.companyName.required')),
            nip: Yup.string()
                .required(t('setup.form.nip.required'))
                .test('valid-nip', t('setup.form.nip.invalid'), isValidNIP),
            email: Yup.string().email().required(t('setup.form.email.required')),
            krs: Yup.string().test('valid-krs', t('setup.form.krs.invalid'), isValidKRS),
            regon: Yup.string().test('valid-regon', t('setup.form.regon.invalid'), isValidREGON),
            vatPayer: Yup.string().required(t('setup.form.vatPayer.required')),
            country: Yup.string().required(t('setup.form.country.required')),
            postalCode: Yup.string()
                .required(t('setup.form.postalCode.required'))
                .test('valid-postal', t('setup.form.postalCode.invalid'), isValidPostalCode),
            city: Yup.string().required(t('setup.form.city.required')),
            street: Yup.string().required(t('setup.form.street.required')),
            building: Yup.string().required(t('setup.form.building.required')),
            apartment: Yup.string(), // jeśli opcjonalne
            comments: Yup.string(),  // jeśli opcjonalne
        })
    });

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [ksefValidationStatus, setKsefValidationStatus] = useState(config?.ksefStatus || 'none');
    const [isValidating, setIsValidating] = useState(false);

    useEffect(() => {
        setKsefValidationStatus(config?.ksefStatus || 'none');
    }, [config?.ksefStatus]);

    const maskToken = (token) => {
        if (!token || token.length < 8) return token;
        return '•'.repeat(token.length - 4) + token.slice(-4);
    };

    const handleValidateKsef = async (token) => {
        if (!token) {
            setKsefValidationStatus('none');
            return;
        }
        setIsValidating(true);
        try {
            const result = await KsefService.validateToken(token);
            if (result.success) {
                setKsefValidationStatus('valid');
                toast({
                    title: t('setup.form.ksef.validation_success'),
                    status: 'success',
                    duration: 3000,
                });
            } else {
                setKsefValidationStatus('error');
                toast({
                    title: t('setup.form.ksef.validation_error'),
                    status: 'error',
                    duration: 3000,
                });
            }
        } catch (error) {
            setKsefValidationStatus('error');
        } finally {
            setIsValidating(false);
        }
    };

    return (
        <>
            <Formik
            enableReinitialize={true}
            validateOnBlur={true}
            validateOnChange={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    const dataToSave = { accountType: 'business', ...values };
                    
                    if (isKsefMarkedForDeletion) {
                        dataToSave.ksefToken = "";
                    } else if (!values.ksefToken) {
                        delete dataToSave.ksefToken;
                    }

                    const success = await setup(dataToSave);
                    if (success) {
                        onSuccess();
                    }
                } catch (e) {
                    toast({
                        title: 'Błąd podczas zapisu danych',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, isValid, dirty }) => (
                <Form>

                    <Flex flexDirection={"column"} gap={'32px'} borderRadius={'12px'}>
                        <Flex flexDirection={"column"} borderRadius={'12px'}>
                            <Flex gap={'32px'} flexDirection={{ base: "column", sm: "row" }}>
                                <Box flex="1" gap={'32px'} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} borderRadius={'12px'}>
                                    <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                        <Text className="title">
                                            {t('setup.form.basic.title')}
                                        </Text>
                                        <Text className="description">
                                            {t('setup.form.basic.description')}
                                        </Text>
                                    </Flex>

                                    <Stack spacing={4}>
                                        <InlineErrorInput name="companyName" label={t('setup.form.companyName.label')} value={values.companyName}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.companyName} touched={touched.companyName} isRequired
                                        />
                                        <InlineErrorInput name="nip" label={t('setup.form.nip.label')} value={values.nip}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.nip} touched={touched.nip} isRequired
                                        />

                                        <FormControl isInvalid={errors.country && touched.country} isRequired={true}>
                                            <FormLabel>{t('setup.form.country.label')}</FormLabel>
                                            <SelectCountry value={values.country}
                                                onChange={(val) => {
                                                    setFieldValue('country', val, true);
                                                }}
                                            />
                                            <FormErrorMessage>{errors.country}</FormErrorMessage>
                                        </FormControl>

                                        <Flex gap={4}>
                                            <InlineErrorInput name="postalCode" label={t('setup.form.postalCode.label')} value={values.postalCode}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.postalCode} touched={touched.postalCode} isRequired
                                            />
                                            <InlineErrorInput name="city" label={t('setup.form.city.label')} value={values.city}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.city} touched={touched.city} isRequired
                                            />
                                        </Flex>

                                        <Flex gap={4} alignItems={"flex-end"}>
                                            <Box flex="3">
                                                <InlineErrorInput name="street" label={t('setup.form.street.label')} value={values.street}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.street} touched={touched.street} isRequired
                                                />
                                            </Box>
                                            <Box flex="1">
                                                <InlineErrorInput name="building" label={t('setup.form.building.label')} value={values.building}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.building} touched={touched.building} isRequired
                                                />
                                            </Box>
                                            <Box flex="1">
                                                <InlineErrorInput name="apartment" label={t('setup.form.apartment.label')} value={values.apartment}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.apartment} touched={touched.apartment}
                                                />
                                            </Box>
                                        </Flex>

                                        <Flex gap={4}>
                                            <InlineErrorInput name="krs" label={t('setup.form.krs.label')} value={values.krs}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.krs} touched={touched.krs}
                                            />
                                            <InlineErrorInput name="regon" label={t('setup.form.regon.label')} value={values.regon}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.regon} touched={touched.regon}
                                            />
                                        </Flex>
                                    </Stack>
                                </Box>
                                <Box flex="1" borderRadius={'12px'}>
                                    <Flex h={"full"} flexDirection={"column"} gap={'32px'} justifyContent={"space-between"}>
                                        <Flex flex="1" flexDirection={"column"} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} >
                                            <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                                <Text className="title">
                                                    {t('setup.form.additional.title')}
                                                </Text>
                                                <Text className="description">
                                                    {t('setup.form.additional.description')}
                                                </Text>
                                            </Flex>
                                            <FormControl>
                                                <FormLabel className="comments">
                                                    <Text className="title">
                                                        {t('setup.form.invoice.notes.label')}
                                                    </Text>
                                                </FormLabel>
                                                <Textarea name="comments" value={values.comments} onChange={handleChange} onBlur={handleBlur} />
                                            </FormControl>
                                        </Flex>
                                        <Flex flex="1" flexDirection={"column"} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} >
                                            <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                                <Text className="title">
                                                    {t('setup.form.payment-integration.title')}
                                                </Text>
                                                <Text className="description">
                                                    {t('setup.form.payment-integration.description')}
                                                </Text>
                                            </Flex>
                                            <Flex flexDirection="column" gap="16px">
                                                <InlineErrorInput name="payment" label={t('setup.form.payment-integration.label')} value={values.payment}
                                                    placeholder={t('setup.form.payment-integration.placeholder')}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.payment} touched={touched.payment}
                                                />
                                                <InlineErrorInput name="paymentSecret" label={t('setup.form.payment-integration.secret.label')} value={values.paymentSecret}
                                                    placeholder={t('setup.form.payment-integration.secret.placeholder')}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.paymentSecret} touched={touched.paymentSecret}
                                                />
                                            </Flex>
                                        </Flex>

                                        {/* KSeF Integration Section */}
                                        <Flex flex="1" flexDirection={"column"} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} mt="32px">
                                            <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                                <Flex justify="space-between" align="center">
                                                    <Text className="title">
                                                        {t('setup.form.ksef.title')}
                                                    </Text>
                                                    <Flex align="center" gap={2}>
                                                        {isValidating ? (
                                                            <Spinner size="xs" />
                                                        ) : (
                                                            <Tooltip label={t(`setup.form.ksef.status.${ksefValidationStatus}`)}>
                                                                {ksefValidationStatus === 'valid' ? (
                                                                    <CheckCircleIcon color="brand.500" boxSize="14px" />
                                                                ) : (
                                                                    <Box
                                                                        w="12px"
                                                                        h="12px"
                                                                        borderRadius="full"
                                                                        bg={
                                                                            ksefValidationStatus === 'error' ? 'red.500' : 'gray.300'
                                                                        }
                                                                    />
                                                                )}
                                                            </Tooltip>
                                                        )}
                                                        <Text 
                                                            fontSize="xs" 
                                                            color={ksefValidationStatus === 'valid' ? 'brand.500' : 'gray.500'}
                                                            fontWeight={ksefValidationStatus === 'valid' ? '600' : '400'}
                                                        >
                                                            {t(`setup.form.ksef.status.${ksefValidationStatus}`)}
                                                        </Text>
                                                    </Flex>
                                                </Flex>
                                                <Text className="description" mt={2}>
                                                    {t('setup.form.ksef.description', 'Zintegruj swoje konto z KSeF, aby automatycznie wystawiać faktury dla zakupów firmowych.')}
                                                </Text>
                                            </Flex>

                                            <Flex flexDirection="column" gap="16px">
                                                <FormControl>
                                                    <Flex justify="space-between" align="center" mb={2}>
                                                        <FormLabel mb={0}>{t('setup.form.ksef.label')}</FormLabel>
                                                        <Button
                                                            variant="link"
                                                            size="xs"
                                                            color="brand.500"
                                                            leftIcon={<InfoIcon />}
                                                            onClick={onOpen}
                                                            _hover={{ textDecoration: 'none', color: 'brand.600' }}
                                                        >
                                                            {t('setup.form.ksef.instruction_link')}
                                                        </Button>
                                                    </Flex>
                                                    <InputGroup>
                                                        <InlineErrorInput
                                                            name="ksefToken"
                                                            value={values.ksefToken}
                                                            placeholder={config?.ksefToken && !isKsefMarkedForDeletion ? maskToken(config.ksefToken) : t('setup.form.ksef.placeholder')}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                if (isKsefMarkedForDeletion) setIsKsefMarkedForDeletion(false);
                                                            }}
                                                            onBlur={(e) => {
                                                                handleBlur(e);
                                                                if (values.ksefToken && values.ksefToken !== config?.ksefToken) {
                                                                    handleValidateKsef(values.ksefToken);
                                                                }
                                                            }}
                                                            error={errors.ksefToken}
                                                            touched={touched.ksefToken}
                                                            useFormik={true}
                                                        />
                                                        <InputRightElement top="8px" width={config?.ksefToken && !isKsefMarkedForDeletion ? "4rem" : "2.5rem"}>
                                                            <Flex gap={2} align="center" justify="flex-end" w="full" pr={2}>
                                                                {ksefValidationStatus === 'valid' && !isKsefMarkedForDeletion && (
                                                                    <CheckCircleIcon color="brand.500" />
                                                                )}
                                                                {ksefValidationStatus === 'error' && !isKsefMarkedForDeletion && (
                                                                    <WarningIcon color="red.500" />
                                                                )}
                                                                {config?.ksefToken && !isKsefMarkedForDeletion && (
                                                                    <Tooltip label={t('setup.form.ksef.delete_connection')}>
                                                                        <IconButton
                                                                            aria-label="Delete integration"
                                                                            icon={<DeleteIcon />}
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            colorScheme="red"
                                                                            onClick={onDeleteAlertOpen}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Flex>
                                                        </InputRightElement>
                                                    </InputGroup>
                                                </FormControl>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Box>
                            </Flex>
                        </Flex>

                        <InvoiceDetailsForm />

                        <Flex w={"full"} justify={"center"}>
                            <Button type="submit" isDisabled={!isValid || isEqual(values, initialValues)} bg="brand.500" _hover={{ bg: 'brand.600' }} h={'56px'}>
                                <Text mr={'10px'}>
                                    {t(config ? 'setup.form.save' : 'setup.form.submit')}
                                </Text>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5.66667V8M8 8V10.3333M8 8H10.3333M8 8H5.66667M15 8C15 8.91925 14.8189 9.82951 14.4672 10.6788C14.1154 11.5281 13.5998 12.2997 12.9497 12.9497C12.2997 13.5998 11.5281 14.1154 10.6788 14.4672C9.82951 14.8189 8.91925 15 8 15C7.08075 15 6.1705 14.8189 5.32122 14.4672C4.47194 14.1154 3.70026 13.5998 3.05025 12.9497C2.40024 12.2997 1.88463 11.5281 1.53284 10.6788C1.18106 9.82951 1 8.91925 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Button>
                        </Flex>
                    </Flex>
                </Form>
            )}
        </Formik>

        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{t('setup.form.ksef.modal_title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <Stack spacing={3}>
                        <Text>{t('setup.form.ksef.instruction.step1')}</Text>
                        <Text>{t('setup.form.ksef.instruction.step2')}</Text>
                        <Text>{t('setup.form.ksef.instruction.step3')}</Text>
                        <Text>{t('setup.form.ksef.instruction.step4')}</Text>
                        <Text>{t('setup.form.ksef.instruction.step5')}</Text>
                        <Text>{t('setup.form.ksef.instruction.step6')}</Text>
                        <Box p={3} bg="orange.50" borderRadius="md" borderLeft="4px solid" borderColor="orange.400">
                            <Text fontSize="sm" color="orange.800">
                                <strong>{t('setup.form.ksef.instruction.note')}</strong>
                            </Text>
                        </Box>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="brand" onClick={onClose}>
                        {t('close')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <AlertDialog
            isOpen={isDeleteAlertOpen}
            leastDestructiveRef={cancelRef}
            onClose={onDeleteAlertClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                        {t('setup.form.ksef.delete_confirm_title')}
                    </AlertDialogHeader>

                    <AlertDialogBody>
                        {t('setup.form.ksef.delete_confirm_body')}
                    </AlertDialogBody>

                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                            {t('cancel')}
                        </Button>
                        <Button colorScheme='red' onClick={() => {
                            setIsKsefMarkedForDeletion(true);
                            setKsefValidationStatus('none');
                            onDeleteAlertClose();
                        }} ml={3}>
                            {t('setup.form.ksef.delete_action')}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
        </>
    );
};

export default CompanyDetailsForm;
